import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mic, Send } from "lucide-react";
import { SPEAKING_TOPICS } from "./speakingTopics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EVENT_TYPES = ["Conference", "Church / Ministry", "Podcast / Interview", "Workshop / Retreat", "Panel", "Other"];
const AUDIENCE_SIZES = ["Under 50", "50 – 150", "150 – 500", "500+", "Virtual / Unlimited"];
const BUDGET_RANGES = ["Under $500", "$500 – $1,000", "$1,000 – $2,500", "$2,500+", "Love offering / To discuss"];
const LOCATIONS = ["In-person", "Virtual", "Hybrid"];

const initial = {
    name: "",
    email: "",
    organization: "",
    topic: "",
    event_date: "",
    event_type: "",
    audience_size: "",
    budget_range: "",
    location_type: "",
    message: "",
};

const fieldCls =
    "w-full h-11 rounded-2xl px-4 bg-white border border-lilac-200 focus:border-gold focus:outline-none text-sm text-lilac-900 placeholder:text-lilac-700/50 transition-colors";

const Label = ({ children }) => (
    <label className="block text-[10px] uppercase tracking-[0.18em] text-lilac-700 font-bold mb-1.5">{children}</label>
);

export const BookingForm = ({ topic = "" }) => {
    const [form, setForm] = useState(initial);
    const [sending, setSending] = useState(false);
    const [sent, setSent] = useState(false);

    useEffect(() => {
        if (topic) setForm((f) => ({ ...f, topic }));
    }, [topic]);

    const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name.trim() || !form.email.trim()) {
            toast.error("Please share your name and email so Coach Melissa can reply.");
            return;
        }
        setSending(true);
        try {
            await axios.post(`${API}/booking`, form);
            setSent(true);
            toast.success("Booking request sent! Coach Melissa will be in touch. 💜");
        } catch {
            toast.error("Something went wrong — please try again or email directly.");
        } finally {
            setSending(false);
        }
    };

    if (sent) {
        return (
            <div
                data-testid="booking-form-success"
                className="rounded-[32px] bg-gradient-to-br from-[#FFF7E5] via-white to-[#FAF5FC] border border-gold/30 p-10 md:p-14 text-center"
            >
                <span className="mx-auto grid place-items-center w-14 h-14 rounded-full bg-gold text-white">
                    <Mic className="w-6 h-6" />
                </span>
                <h3 className="mt-6 font-display text-3xl text-lilac-900">
                    Request <em className="italic gold-text">received</em>.
                </h3>
                <p className="mt-4 text-lilac-700 leading-relaxed max-w-md mx-auto">
                    Thank you for the invitation! Coach Melissa reviews every booking request personally and
                    will reply to your email soon.
                </p>
            </div>
        );
    }

    return (
        <form
            onSubmit={submit}
            data-testid="booking-form"
            className="rounded-[32px] bg-white border border-lilac-200 p-8 md:p-12"
        >
            <div className="eyebrow text-gold-deep flex items-center gap-2">
                <Mic className="w-3.5 h-3.5" /> Speaker booking
            </div>
            <h2 className="mt-3 font-display text-3xl md:text-4xl">
                Invite Coach Melissa to <em className="italic gold-text">your stage</em>.
            </h2>
            <p className="mt-4 text-sm text-lilac-700 max-w-xl leading-relaxed">
                Conferences, church events, podcasts, workshops — share the details below and she'll get back to you personally.
            </p>

            <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                    <Label>Your name *</Label>
                    <input data-testid="booking-name" className={fieldCls} value={form.name} onChange={set("name")} placeholder="Full name" />
                </div>
                <div>
                    <Label>Email *</Label>
                    <input data-testid="booking-email" type="email" className={fieldCls} value={form.email} onChange={set("email")} placeholder="you@example.com" />
                </div>
                <div>
                    <Label>Organization / Ministry</Label>
                    <input data-testid="booking-organization" className={fieldCls} value={form.organization} onChange={set("organization")} placeholder="Organization name" />
                </div>
                <div>
                    <Label>Event date</Label>
                    <input data-testid="booking-event-date" type="date" className={fieldCls} value={form.event_date} onChange={set("event_date")} />
                </div>
                <div className="md:col-span-2">
                    <Label>Requested talk</Label>
                    <select data-testid="booking-topic" className={fieldCls} value={form.topic} onChange={set("topic")}>
                        <option value="">Open to Coach Melissa's recommendation</option>
                        {SPEAKING_TOPICS.map((t) => <option key={t.title} value={t.title}>{t.title}</option>)}
                    </select>
                </div>
                <div>
                    <Label>Event type</Label>
                    <select data-testid="booking-event-type" className={fieldCls} value={form.event_type} onChange={set("event_type")}>
                        <option value="">Select…</option>
                        {EVENT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                </div>
                <div>
                    <Label>Audience size</Label>
                    <select data-testid="booking-audience-size" className={fieldCls} value={form.audience_size} onChange={set("audience_size")}>
                        <option value="">Select…</option>
                        {AUDIENCE_SIZES.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                </div>
                <div>
                    <Label>Budget / honorarium</Label>
                    <select data-testid="booking-budget" className={fieldCls} value={form.budget_range} onChange={set("budget_range")}>
                        <option value="">Select…</option>
                        {BUDGET_RANGES.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                </div>
                <div>
                    <Label>Location</Label>
                    <select data-testid="booking-location" className={fieldCls} value={form.location_type} onChange={set("location_type")}>
                        <option value="">Select…</option>
                        {LOCATIONS.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                </div>
                <div className="md:col-span-2">
                    <Label>Event details / message</Label>
                    <textarea
                        data-testid="booking-message"
                        rows={5}
                        className="w-full rounded-2xl px-4 py-3 bg-white border border-lilac-200 focus:border-gold focus:outline-none text-sm text-lilac-900 placeholder:text-lilac-700/50 transition-colors resize-y"
                        value={form.message}
                        onChange={set("message")}
                        placeholder="Tell us about your event, theme, and what you'd love Coach Melissa to speak on…"
                    />
                </div>
            </div>

            <button
                type="submit"
                disabled={sending}
                data-testid="booking-submit"
                className="mt-8 inline-flex items-center justify-center gap-2 h-12 px-8 rounded-full bg-lilac-900 hover:bg-lilac-700 disabled:opacity-60 text-white font-semibold text-sm shadow-[0_14px_36px_rgba(43,29,53,0.20)] transition-all"
            >
                <Send className="w-4 h-4" /> {sending ? "Sending…" : "Send booking request"}
            </button>
        </form>
    );
};
